/**
 * MmsMedia — renders one MMS attachment (image / video / file) for a
 * given SMS message. Always fetches through the authenticated backend
 * proxy at /api/sms/media-proxy/{message_id}/{media_index} so raw
 * Twilio media URLs (which require Basic Auth) work in the browser.
 *
 * Props
 * -----
 *   messageId   — the SMS row id (or message_sid) the media belongs to.
 *   index       — position of this attachment in the row's media_urls.
 *   rawUrl      — the original URL (used only to guess the display
 *                 kind — image vs file — before the blob loads).
 *   outbound    — controls colour of the file-link chip fallback.
 *
 * The component owns its own blob URL and revokes it on unmount so we
 * don't leak object URLs across long threads.
 */
import { useEffect, useState } from "react";
import { Paperclip, FileText, ImageOff } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function guessKind(url) {
  const u = String(url || "").toLowerCase();
  if (/\.(png|jpe?g|gif|webp|heic|bmp)(\?|$)/.test(u)) return "image";
  if (/\.(mp4|mov|webm|avi)(\?|$)/.test(u)) return "video";
  // Twilio URLs don't carry an extension — inbound images arrive as
  // `.../Media/MEabc...` with no suffix. Assume image because 99% of
  // real-world MMS is a photo; the fallback chip covers the rest.
  if (u.includes("twilio.com") && u.includes("/media/")) return "image";
  return "file";
}

export default function MmsMedia({ messageId, index, rawUrl, outbound }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [contentType, setContentType] = useState(null);
  const [error, setError] = useState(false);
  const kind = guessKind(rawUrl);

  useEffect(() => {
    let alive = true;
    let objectUrl = null;
    if (!messageId || index == null) return;
    const tok = localStorage.getItem("token");
    const proxied = `${API}/api/sms/media-proxy/${encodeURIComponent(messageId)}/${index}`;
    fetch(proxied, { headers: tok ? { Authorization: `Bearer ${tok}` } : {} })
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const ct = r.headers.get("content-type") || "";
        const b = await r.blob();
        objectUrl = URL.createObjectURL(b);
        if (alive) {
          setBlobUrl(objectUrl);
          setContentType(ct);
        }
      })
      .catch(() => { if (alive) setError(true); });
    return () => {
      alive = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [messageId, index]);

  // Refine kind once the actual content-type comes back — a Twilio
  // URL without an extension might resolve to a PDF or video.
  const resolvedKind = contentType
    ? (contentType.startsWith("image/") ? "image"
      : contentType.startsWith("video/") ? "video"
      : "file")
    : kind;

  if (error) {
    return (
      <div
        className={`flex items-center gap-1.5 text-[11px] italic ${outbound ? "text-white/70" : "text-slate-500"}`}
        data-testid="mms-media-error"
      >
        <ImageOff className="w-3.5 h-3.5" />
        Attachment couldn&apos;t load
      </div>
    );
  }

  if (!blobUrl) {
    return (
      <div
        className={`flex items-center gap-1.5 text-[11px] ${outbound ? "text-white/70" : "text-slate-400"}`}
        data-testid="mms-media-loading"
      >
        <Paperclip className="w-3.5 h-3.5" />
        Loading attachment…
      </div>
    );
  }

  if (resolvedKind === "image") {
    return (
      <a href={blobUrl} target="_blank" rel="noopener noreferrer" className="block" data-testid="mms-media-image">
        <img
          src={blobUrl}
          alt="MMS attachment"
          className="max-w-full rounded-lg max-h-[260px] object-contain bg-black/5 cursor-pointer hover:opacity-90 transition-opacity"
        />
      </a>
    );
  }

  if (resolvedKind === "video") {
    return (
      <video
        src={blobUrl}
        controls
        className="max-w-full rounded-lg max-h-[260px]"
        data-testid="mms-media-video"
      />
    );
  }

  // Generic file — link out with an icon chip.
  return (
    <a
      href={blobUrl}
      target="_blank"
      rel="noopener noreferrer"
      download
      className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-xs underline ${outbound ? "bg-white/10 text-white" : "bg-white text-slate-700 border"}`}
      data-testid="mms-media-file"
    >
      <FileText className="w-3.5 h-3.5 shrink-0" />
      Open attachment
    </a>
  );
}
